import { getCookie } from "./cookieUtils";
import { useState, useEffect, useRef } from "react";
import LoginBox from "./LoginBox";
import TrackingBox from "./TrackingBox";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Pagination } from 'antd';
import { Pencil, Trash2 } from 'lucide-react';


const ClientLogin = () => {
    const inputrefs = useRef([]);
    const [loginToken, setLoginToken] = useState(getCookie("loginToken") || null);
    const [userName, setUserName] = useState(getCookie("userName") || "");
    const [formData, setFormData] = useState({
        userId: "",
        password: "",
        userType: "",
        isUpdate: false
    });
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentEditId, setCurrentEditId] = useState(null);
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [showPassword, setShowPassword] = useState(false);

    // Common headers for API calls
    const apiConfig = {
        AuthToken: "NASSKD28462RWKF35793FWEU757",
        LoginUserCode: userName,
        LoginToken: loginToken,
        PageIndex: String(currentPage),
        PageSize: String(pageSize),
        Search: search
    };

    // Set initial focus
    useEffect(() => {
        if (inputrefs.current["txtUserID"]) {
            inputrefs.current["txtUserID"].focus();
        }
    }, []);

    // Fetch all users on component load
    useEffect(() => {
        if (loginToken) {
            fetchUsers();
        }
    }, [loginToken, currentPage, pageSize]);

    // Show/Fetch all users
    const fetchUsers = async () => {
        setLoading(true);
        try {

            const payload = {
                ...apiConfig,
            };

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/User/ShowUserMaster`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const resData = await response.json();
            setUsers(resData.Data || []);
            setTotalRecords(Number(resData.TotalRecord) || 0);
            //setPageSize(Number(resData.PageSize) || 10);

        } catch (error) {
            console.error("Error fetching users:", error);
            toast.error("Failed to fetch users.");
        } finally {
            setLoading(false);
        }
    };

    // Save or Update user
    const handleSave = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                ...apiConfig,
                UserID:  formData.userId,
                Password: btoa(formData.password),
                UserType: formData.userType,
                isUpdate: editMode
            };

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/User/InsertUpdateUserMaster`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.status === "SUCCESS") {
                toast.success(data?.message);
                resetForm();
                fetchUsers();
            } else if (data.status === "ERROR") {
                toast.error(data?.message);
                const focusField = data?.focus || data?.Focus;
                if (focusField && inputrefs.current[focusField]) {
                    inputrefs.current[focusField].focus();
                }
            }

        } catch (error) {
            console.error("Error saving user:", error);
            toast.error("Failed to save user. Please check the console for details.");
        } finally {
            setLoading(false);
        }
    };

    // Edit user
    const handleEdit = async (userId) => {
        setLoading(true);
        try {
            const payload = {
                ...apiConfig, 
                UserID: userId,
            };

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/User/EditUserMaster`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const userData = await response.json();
            if (userData.status === "SUCCESS") {
                setFormData({
                    userId: userData.userID ,
                    password: userData.password ,
                    userType: userData.userType 
                });
            } else {
                setFormData({
                    userId:  "",
                    password:  "",
                    userType:  ""
                });
            }
            setEditMode(true);
            //setCurrentEditId(userData.UserID);

            // Scroll to form
            document.getElementById('userForm')?.scrollIntoView({ behavior: 'smooth' });
        } catch (error) {
            console.error("Error fetching user details:", error);
            toast.error("Failed to fetch user details for editing.");
        } finally {
            setLoading(false);
        }
    };

    // Delete user
    const handleDelete = async (userId) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, delete it!"
        });

        if (!result.isConfirmed) return;

        setLoading(true);

        try {
            const payload = {
                ...apiConfig, 
                UserID: userId
            };

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/User/DeleteUserMaster`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            await response.json();
            await Swal.fire({
                title: "Deleted!",
                text: "User has been deleted successfully.",
                icon: "success",
                timer: 1500,
                showConfirmButton: false
            });

            fetchUsers();

        } catch (error) {
            console.error("Error deleting user:", error);

            // ? Error toast.error
            Swal.fire({
                title: "Error!",
                text: "Failed to delete user.",
                icon: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleLoginSuccess = (loginData) => {
        setLoginToken(loginData.loginToken);
        setUserName(loginData.userName); 
        fetchUsers();
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            userId: "",
            password: "",
            userType: ""
        });
        setEditMode(false);
        setCurrentEditId(null);
        setUsers([]);
        setTotalRecords(0);
        setCurrentPage(1);
        setSearch("");
        setTimeout(() => {
            if (inputrefs.current["txtUserID"]) inputrefs.current["txtUserID"].focus();
        }, 0);
    };

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    return (
        <>
<section>
                <div className="container-fluid">
                    <img src="images/innerbanner.webp" className='img-fluid' alt="" />
                </div>
            </section>

            {/* MAIN SECTION */}
            <section className="main-section">
                <div className="container">
                    <div className="row g-4">

                        {/* SIDEBAR */}
                        <div className="col-lg-3">
                            <TrackingBox />
                            <LoginBox onLoginSuccess={handleLoginSuccess} />

                            {/* Contact */}
                            <div className="contact-card">
                                <div className="contact-title">Contact</div>
                                <div className="contact-item">
                                    <div className="contact-icon phone">
                                        <i className="fa fa-phone"></i>
                                    </div>
                                    <a href="tel:01141606842">+91 96672 33072</a>
                                </div>
                                <div className="contact-item">
                                    <div className="contact-icon email">
                                        <i className="fa fa-envelope"></i>
                                    </div>
                                    <a href="#">oxford_express@yahoo.co.in</a>
                                </div>
                                <div className="contact-item">
                                    <div className="contact-icon email">
                                        <i className="fa fa-envelope"></i>
                                    </div>
                                    <a href="#">info@oxfordexp.com</a>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-8">
                            {/* User Form */}
                            <div id="userForm">
                                <h4 className="mb-3">User Master</h4>
                                <form>
                                    <div className="row g-3">
                                        <div className="col-md-4">
                                            <div className="login-field mb-0">
                                                <label>User ID<span className="text-danger">*</span></label>
                                                <input
                                                    id="txtUserID"
                                                    ref={(el) => (inputrefs.current["txtUserID"] = el)}
                                                    type="text"
                                                    name="userId"
                                                    className="track-input"
                                                    placeholder="Enter User ID"
                                                    value={formData.userId}
                                                    onChange={handleInputChange}
                                                    disabled={editMode}
                                                    required
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            inputrefs.current["txtPassword"]?.focus();
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        <div className="col-md-4">
                                            <div className="login-field mb-0">
                                                <label>Password<span className="text-danger">*</span></label>

                                                <div className="position-relative">
                                                    <input
                                                        id="txtPassword"
                                                        ref={(el) => (inputrefs.current["txtPassword"] = el)}
                                                        type={showPassword ? "text" : "password"}
                                                        name="password"
                                                        className="track-input m-0"
                                                        style={{ paddingRight: "40px" }}
                                                        placeholder="Enter Password"
                                                        value={formData.password}
                                                        onChange={handleInputChange}
                                                        required
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") {
                                                                e.preventDefault();
                                                                inputrefs.current["txtUserType"]?.focus();
                                                            }
                                                        }}
                                                    />
                                                    <span
                                                        className="position-absolute cursor-pointer"
                                                        style={{ right: "12px", top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", color: "#6b7c99" }}
                                                        onClick={() => setShowPassword(!showPassword)}
                                                    >
                                                        <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`} style={{ fontSize: "18px" }}></i>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-4">
                                            <div className="login-field mb-0">
                                                <label>User Type<span className="text-danger">*</span></label>
                                                <select
                                                    id="txtUserType"
                                                    ref={(el) => (inputrefs.current["txtUserType"] = el)}
                                                    name="userType"
                                                    className="track-input"
                                                    value={formData.userType}
                                                    onChange={handleInputChange}
                                                    required
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            inputrefs.current["btnSub"]?.focus();
                                                        }
                                                    }}
                                                >
                                                    <option value="">Select User Type</option>
                                                    <option value="0">Client</option>
                                                    <option value="1">Admin</option>
                                                    <option value="2">Branch</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="d-flex gap-3 mt-3 w-50">
                                        <button id="btnSub" type="submit" className="track-btn" disabled={loading} onClick={handleSave} ref={(el) => (inputrefs.current["btnSub"] = el)}>
                                            <i className="fa fa-save me-1"></i>
                                            {loading ? 'Processing...' : (editMode ? 'Update' : 'Save')}
                                        </button>
                                        <button type="button" className="login-btn" onClick={resetForm}>
                                            <i className="fa fa-undo me-1"></i> Reset
                                        </button>
                                    </div>
                                </form>
                            </div>

                            {/* Users Grid */}
                            <div className="mt-5">

                                {/* ?? Search */}
                                <div className="d-flex justify-content-end mb-3">
                                    {/*<div className="d-flex gap-2 w-50">*/}
                                    {/*    <input*/}
                                    {/*        type="text"*/}
                                    {/*        className="track-input w-50 m-0"*/}
                                    {/*        placeholder="Search user..."*/}
                                    {/*        value={search}*/}
                                    {/*        onChange={(e) => setSearch(e.target.value)}*/}
                                    {/*        onKeyDown={(e) => {*/}
                                    {/*            if (e.key === "Enter") fetchUsers();*/}
                                    {/*        }}*/}
                                    {/*    />*/}
                                    {/*    <button */}
                                    {/*        type="button" */}
                                    {/*        className="track-btn m-0" */}
                                    {/*        onClick={fetchUsers} */}
                                    {/*        disabled={loading}*/}
                                    {/*        style={{ height: "44px", width: "55px" }}*/}
                                    {/*    >*/}
                                    {/*        <i className="fa fa-search me-1"></i>*/}
                                    {/*    </button>*/}
                                    {/*</div>*/}

                                    <select
                                        className="form-select w-auto"
                                        value={pageSize}
                                        onChange={(e) => {
                                            setPageSize(Number(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value={10}>10</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </select>
                                </div>

                                {/* Table */}
                                <div className="table-responsive overflow-x-hidden" style={{ maxHeight: "500px" }}>
                                    {loading ? (
                                        <div className="text-center">
                                            <div className="spinner-border text-primary"></div>
                                        </div>
                                    ) : (
                                        <table className="table table-bordered table-hover">
                                            <thead className="table-dark">
                                                <tr>
                                                        {/*<th>SrNo.</th>*/}
                                                     <th>Actions</th>
                                                    <th>Username</th>
                                                    <th>Access Type</th>
                                                    
                                                </tr>
                                            </thead>
                                            <tbody>
                                                    {users.length > 0 ? (
                                                        users.map((user, index) => (
                                                        <tr key={index}>
                                                                {/*<td>{user.SrNo}</td>*/}
                                                                <td>
                                                                    <button
                                                                        className="btn btn-sm"
                                                                        onClick={() => handleEdit(user.Username)}
                                                                    >
                                                                        <Pencil size={18} className="text-primary" />
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm"
                                                                        onClick={() => handleDelete(user.Username)}
                                                                    >
                                                                        <Trash2 size={18} className="text-danger" />
                                                                    </button>
                                                                </td>
                                                            <td>{user.Username}</td>
                                                                <td>
                                                                    {user.UserType}
                                                                  
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="4" className="text-center">
                                                            No users found
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    )}
                                </div>

                                {/* ?? Pagination */}
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div className="text-muted">
                                        Showing {users.length} of {totalRecords} records
                                    </div>

                                    <Pagination
                                        current={currentPage}
                                        total={totalRecords}
                                        pageSize={pageSize}
                                        onChange={(page) => setCurrentPage(page)}
                                        showSizeChanger={false}  
                                        size="default"  
                                        showQuickJumper={false}
                                    />
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* STATS STRIP */}
            <section className="stats-strip">
                <div className="container">
                    <div className="row g-0 text-center">
                        <div className="col-6 col-md-3 stat-item py-2">
                            <div className="stat-number">4.5L+</div>
                            <div className="stat-label">Packets / Month</div>
                        </div>
                        <div className="col-6 col-md-3 stat-item py-2">
                            <div className="stat-number">1000+</div>
                            <div className="stat-label">Cities Covered</div>
                        </div>
                        <div className="col-6 col-md-3 stat-item py-2">
                            <div className="stat-number">35+</div>
                            <div className="stat-label">Years Experience</div>
                        </div>
                        <div className="col-6 col-md-3 stat-item py-2">
                            <div className="stat-number">10K+</div>
                            <div className="stat-label">Happy Clients</div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default ClientLogin;

